import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import HSTSCustomStyle from "../../utils/hstsCustomStyle";
import DataTable from "react-data-table-component";
import { TopHeader } from "./inc/topHeader";
import { SideBar } from "./inc/sideBar";
import {
  getAllStudents,
  updateStudentStatus,
} from "../../services/StudentService";
import { ToastContainer } from "react-toastify";
import { HSTSToast } from "../../utils/hstsToast";

const ManageStudent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const navigator = useNavigate();
  const [students, setStudent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [perPage, setPerPage] = useState(5);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const fetchAllStudents = () => {
    setLoading(true);
    getAllStudents()
      .then((response) => {
        const studentsData = response.data?.data || [];
        setStudent(studentsData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        setError("Failed to fetch students data");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAllStudents();
  }, []);

  const viewMore = (id) => navigator(`/admin/studentDetails/${id}`);

  const filteredStudents = students.filter((student) => {
    const searchTerm = searchText.toLowerCase();
    return (
      (student.full_name?.toLowerCase() || "").includes(searchTerm) ||
      (student.phone_number?.toLowerCase() || "").includes(searchTerm) ||
      (student.email_address?.toLowerCase() || "").includes(searchTerm) ||
      (student.gender?.toLowerCase() || "").includes(searchTerm)
    );
  });

  const authHandler = async (studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    if (!student) return;

    const currentStatus = student.student_status?.toLowerCase();
    const newStatus = currentStatus === "enable" ? "disable" : "enable";
    const actionText = newStatus === "enable" ? "Enable" : "Disable";

    const confirmAction = window.confirm(
      `Are you sure you want to ${actionText} this student?`
    );

    if (!confirmAction) {
      alert("Action cancelled.");
      return;
    }

    try {
      await updateStudentStatus(studentId, newStatus);

      setStudent((prev) =>
        prev.map((s) =>
          s.student_id === studentId ? { ...s, student_status: newStatus } : s
        )
      );

      HSTSToast("success", `Student ${actionText}d successfully.`);
    } catch (error) {
      console.error("Error updating student status:", error);
      HSTSToast("error", "Failed to update student status. Please try again.");
    }
  };

  const columns = [
    {
      name: "S/N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Student name",
      selector: (row) =>
        (row.full_name || "N/A")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
    },
    {
      name: "Phone number",
      selector: (row) => row.phone_number,
      width: "160px",
    },
    {
      name: "Email",
      selector: (row) => row.email_address,
    },
    {
      name: "Gender",
      selector: (row) => row.gender,
      width: "120px",
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => {
        const status = row.student_status?.toLowerCase();
        let displayText = row.student_status || "N/A";
        let badgeClass = "bg-secondary text-white";

        if (status === "enable") {
          displayText = "Active";
          badgeClass = "bg-success text-white";
        } else if (status === "disable") {
          displayText = "In Active";
          badgeClass = "bg-danger text-white";
        }
        return (
          <div className="text-center justify-content-center align-items-center">
            <span className={`badge fw-bold fs-6 ${badgeClass}`}>
              {displayText}
            </span>
          </div>
        );
      },
    },
    {
      name: "View",
      cell: (row) => (
        <button
          className="btn btn-link text-center fs-4 text-primary zis-ponter"
          title="Manage student"
          onClick={() => viewMore(row.student_id)}
        >
          <i className="fas fa-user-cog"></i>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
    {
      name: "Action",
      cell: (row) => {
        const isEnabled = row.student_status?.toLowerCase() === "enable";
        return (
          <button
            className={`btn btn-link text-center fs-4 ${
              isEnabled ? "text-danger" : "text-success"
            } zis-ponter`}
            title={isEnabled ? "Disable student" : "Enable student"}
            onClick={() => authHandler(row.student_id)}
          >
            <i
              className={`fas ${isEnabled ? "fa-user-times" : "fa-user-check"}`}
            ></i>
          </button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];

  return (
    <>
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />

          <div className="container mt-0">
            <div className="page-inner">
              <div className="page-header bg-white zis-shadow rounded-2 mt-2 ">
                <ul className="breadcrumbs my-md-3 mb-3  ms-0">
                  <li className="nav-home">
                    <Link to="/admin" className="ps-3 ps-md-0">
                      <span className="fa fa-home"></span>
                    </Link>
                  </li>
                  <li className="separator">
                    <i class="fas fa-chevron-right text-secondary"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <Link to="#">Manage students</Link>
                  </li>
                </ul>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="card zis-shadow">
                    <div className="card-header">
                      <div className="d-flex mt-4 align-items-center">
                        <div className="card-title">
                          <h3 className="mt-2">Manage students</h3>
                        </div>
                        <div className="ms-auto d-sm-none align-items-center">
                          <div className="d-flex">
                            <span className="me-2">Show</span>
                            <select
                              className="form-select form-select-sm"
                              style={{ width: "80px" }}
                              value={perPage}
                              onChange={(e) =>
                                setPerPage(Number(e.target.value))
                              }
                            >
                              <option value="5">5</option>
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                            </select>
                            <span className="ms-2">entries</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-header d-flex justify-content-between align-items-center pt-4 px-4">
                      <div className="align-items-center">
                        <div className="d-none d-sm-flex">
                          <span className="me-2">Show</span>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: "80px" }}
                            value={perPage}
                            onChange={(e) => setPerPage(Number(e.target.value))}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                          </select>
                          <span className="ms-2">entries</span>
                        </div>
                      </div>
                      <div className="d-flex">
                        <input
                          type="text"
                          className="form-control form-control-sm me-2 "
                          placeholder="Search..."
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "200px" }}
                        />
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        {error ? (
                          <div className="text-center text-danger py-3">
                            {error}
                          </div>
                        ) : (
                          <DataTable
                            key={`data-table-${perPage}`}
                            columns={columns}
                            data={filteredStudents}
                            progressPending={loading}
                            progressComponent={
                              <div className="text-center my-3">
                                Loading students...
                              </div>
                            }
                            noDataComponent={
                              <div className="text-center py-3">
                                No students available
                              </div>
                            }
                            pagination
                            paginationPerPage={perPage}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            customStyles={HSTSCustomStyle}
                            highlightOnHover
                            responsive
                          />
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    </>
  );
};

export default ManageStudent;
